import { useCallback, useMemo, useRef } from 'react'
import type { PointerEvent as ReactPointerEvent } from 'react'
import type { BlockState } from '../VisitBlock.js'
import { SLOT_MINUTES, minutesBetween } from './geometry.js'
import { useBoardGestures } from './interactions.js'
import type { BoardGestures } from './interactions.js'
import type { Move } from './optimistic.js'
import { bucketIdOf, resourceOfVisit, userIdOf, type ResourceId } from './resource.js'
import { useSelection, type SelectionApi } from './selection.js'
import type { BoardVisit, Placement, SchedulablePerson, UnscheduledJob } from './types.js'

/**
 * One object the views hand to their blocks.
 *
 * Selection, dragging, resizing and the keyboard are separate machines with
 * separate concerns, but a block needs all four at once. Composing them here
 * means `DayBoard` and `TimelineBoard` pass one prop instead of nine, and the
 * two views cannot drift in how a block responds to a click.
 */

export interface BoardController {
  gestures: BoardGestures
  selection: SelectionApi
  /** Blocks report their element so a marquee can find them. */
  registerBlock: (id: string, element: HTMLElement | null) => void
  /** How a block should be skinned right now. */
  blockState: (visit: BoardVisit, rowUserId?: string | null) => BlockState
  /** The live time range while a gesture is on this block, otherwise null. */
  blockLabel: (visit: BoardVisit, rowUserId?: string | null) => string | null
  onBlockPointerDown: (
    event: ReactPointerEvent,
    visit: BoardVisit,
    rowUserId?: string | null,
  ) => void
  onBlockKeyDown: (event: React.KeyboardEvent, visit: BoardVisit) => void
  /** Pointer-down on empty grid: a span, or a marquee when a modifier is held. */
  onEmptyPointerDown: (event: ReactPointerEvent, resourceId: ResourceId) => void
  /** Slot left on the board after a span, until Book / Add event / cancel. */
  draftSpan: (Placement & { label: string }) | null
  /** Visits with any live preview applied, ready to be grouped and drawn. */
  effective: BoardVisit[]
  /** The board row a visit currently belongs to, preview included. */
  rowOf: (visit: BoardVisit) => ResourceId
  canEdit: boolean
}

export function useBoardController({
  windowStart,
  axis,
  slotPx,
  visits,
  people,
  scrollerRef,
  canEdit,
  onMove,
  onCreate,
  onPark,
  onParkVisits,
  canPark,
  onOpen,
  onQuickMenu,
  onSplit,
  onSpan,
  draftSpan = null,
}: {
  windowStart: Date
  axis: 'y' | 'x'
  slotPx: number
  visits: BoardVisit[]
  people: SchedulablePerson[]
  scrollerRef: React.RefObject<HTMLElement | null>
  canEdit: boolean
  onMove: (moves: Move[]) => void
  onCreate: (job: UnscheduledJob, placement: Placement) => void
  onPark: (job: UnscheduledJob, bucketId: string) => void
  /** A booking dragged into a bucket: the visit goes and the job is filed. */
  onParkVisits?: (entries: Array<{ visitId: string; bucketId: string }>) => void
  /** Whether a block is a booking with a job to file. Events are not. */
  canPark?: (visitId: string) => boolean
  onOpen: (visitId: string) => void
  onQuickMenu?: (visitId: string, point: { x: number; y: number }) => void
  onSplit?: (input: {
    id: string
    userId: string
    startAt: Date
    endAt: Date
    landOnUserId?: string
  }) => void
  onSpan?: (placement: Placement & { label: string }, point: { x: number; y: number }) => void
  draftSpan?: (Placement & { label: string }) | null
}): BoardController {
  const blocks = useRef(new Map<string, HTMLElement>())
  const selection = useSelection({ rects: blocks })

  const gestures = useBoardGestures({
    windowStart,
    axis,
    slotPx,
    visits,
    scrollerRef,
    onMove,
    onSplit,
    onCreate,
    onPark,
    onParkVisits,
    canPark,
    onTap: (id, point) => {
      if (selection.selected.size > 1) return
      onQuickMenu?.(id, point)
    },
    onSpan,
    onEmptyTap: () => selection.clear(),
    enabled: canEdit,
  })

  const byId = useMemo(() => new Map(visits.map((visit) => [visit.id, visit])), [visits])

  const registerBlock = useCallback((id: string, element: HTMLElement | null) => {
    if (element) blocks.current.set(id, element)
    else blocks.current.delete(id)
  }, [])

  /**
   * The visits as they would be if the current gesture were committed.
   *
   * Solo bookings leave the row they came from. A crew stays on every
   * assignee; the grabbed copy is moved in the view so teammates do not
   * vanish and pop back.
   */
  const effective = useMemo(
    () =>
      visits.map((visit) => {
        const placement = gestures.preview.placements.get(visit.id)
        if (!placement) return visit
        // A crew preview leaves the original times in place so teammates
        // do not vanish. The grabbed copy is moved in the view.
        if (gestures.preview.detachUserId || visit.assignments.length > 1) return visit

        const landedOn = userIdOf(placement.resourceId)

        return {
          ...visit,
          startAt: placement.startAt,
          endAt: placement.endAt,
          // Dragged onto a row that is not a person — the unassigned queue, or
          // a bucket — the preview shows it with nobody on it, which is what
          // dropping there will actually do.
          assignments: landedOn
            ? visit.assignments.map((assignment, index) =>
                index === 0
                  ? {
                      ...assignment,
                      userId: landedOn,
                      user: people.find((person) => person.id === landedOn) ?? assignment.user,
                    }
                  : assignment,
              )
            : [],
        }
      }),
    [gestures.preview.detachUserId, gestures.preview.placements, people, visits],
  )

  const rowOf = useCallback(
    (visit: BoardVisit) =>
      gestures.preview.placements.get(visit.id)?.resourceId ?? resourceOfVisit(visit),
    [gestures.preview.placements],
  )

  const blockState = useCallback(
    (visit: BoardVisit, rowUserId?: string | null): BlockState => {
      if (gestures.preview.conflicts.has(visit.id)) return 'conflict'
      if (gestures.preview.placements.has(visit.id)) {
        const travelling = travellingCopy(visit, gestures.preview, rowUserId)
        if (!travelling) return selection.isSelected(visit.id) ? 'selected' : 'idle'
        return 'dragging'
      }
      if (selection.isSelected(visit.id)) return 'selected'
      return 'idle'
    },
    [gestures.preview, selection],
  )

  const blockLabel = useCallback(
    (visit: BoardVisit, rowUserId?: string | null) => {
      if (!gestures.preview.placements.has(visit.id)) return null
      if (!travellingCopy(visit, gestures.preview, rowUserId)) return null
      return gestures.preview.label
    },
    [gestures.preview],
  )

  const onBlockPointerDown = useCallback(
    (event: ReactPointerEvent, visit: BoardVisit, rowUserId?: string | null) => {
      const detach =
        Boolean(event.shiftKey) && visit.assignments.length > 1 && Boolean(rowUserId)
      const additive = event.metaKey || event.ctrlKey || (event.shiftKey && !detach)
      selection.toggle(visit.id, additive)

      if (additive) return

      if (!canEdit) {
        onQuickMenu?.(visit.id, { x: event.clientX, y: event.clientY })
        return
      }

      // Everything currently selected travels with the grabbed block; a block
      // outside the selection travels alone.
      const travelling = selection.selected.has(visit.id)
        ? [...selection.selected].flatMap((id) => byId.get(id) ?? [])
        : [visit]

      gestures.beginMove(event, visit, travelling, {
        originUserId: rowUserId ?? undefined,
        detachUserId: detach ? rowUserId! : undefined,
      })
    },
    [byId, canEdit, gestures, onQuickMenu, selection],
  )

  /**
   * The keyboard equivalent of a drag.
   *
   * Arrows nudge a quarter hour, shift-arrow an hour, brackets resize. The
   * dispatcher who has just found the right slot by eye can adjust it without
   * reaching back for the mouse, and someone who cannot use a mouse at all can
   * work the board.
   */
  const onBlockKeyDown = useCallback(
    (event: React.KeyboardEvent, visit: BoardVisit) => {
      if (!canEdit) return

      const selected = selection.selected.has(visit.id)
        ? [...selection.selected].flatMap((id) => byId.get(id) ?? [])
        : [visit]

      const step = event.shiftKey ? 60 : SLOT_MINUTES

      if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        event.preventDefault()
        const moved = gestures.nudge(selected, event.key === 'ArrowUp' ? -step : step)
        if (moved) onMove(moved.map(toMove))
        return
      }

      if (event.key === '[' || event.key === ']') {
        event.preventDefault()
        const resized = gestures.stretch(visit, event.key === '[' ? -SLOT_MINUTES : SLOT_MINUTES)
        if (resized) onMove([toMove(resized)])
        return
      }

      // Left and right move a block between people, which is the same
      // reassignment a sideways drag performs.
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault()
        const current = visit.assignments[0]?.userId
        const index = people.findIndex((person) => person.id === current)
        if (index < 0) return

        const target = people[index + (event.key === 'ArrowLeft' ? -1 : 1)]
        if (!target) return

        onMove([
          {
            id: visit.id,
            startAt: visit.startAt,
            endAt: visit.endAt,
            userIds: [target.id],
          },
        ])
        return
      }

      if (event.key === 'Enter') {
        event.preventDefault()
        onOpen(visit.id)
      }
    },
    [byId, canEdit, gestures, onMove, onOpen, people, selection],
  )

  const onEmptyPointerDown = useCallback(
    (event: ReactPointerEvent, resourceId: ResourceId) => {
      const additive = event.shiftKey || event.metaKey || event.ctrlKey
      // A bucket has no time axis, so a drag there cannot be a slot. Modifier
      // and view-only keep the lasso people already know from a file manager.
      if (additive || !canEdit || bucketIdOf(resourceId)) {
        selection.beginMarquee(event)
        return
      }

      gestures.beginSpan(event, resourceId)
    },
    [canEdit, gestures, selection],
  )

  return {
    gestures,
    selection,
    registerBlock,
    blockState,
    blockLabel,
    onBlockPointerDown,
    onBlockKeyDown,
    onEmptyPointerDown,
    draftSpan,
    effective,
    rowOf,
    canEdit,
  }
}

function toMove(entry: { id: string; startAt: Date; endAt: Date }): Move {
  return { id: entry.id, startAt: entry.startAt, endAt: entry.endAt }
}

/** The copy under the pointer travels. Teammates stay put. */
function travellingCopy(
  visit: BoardVisit,
  preview: { detachUserId: string | null; originUserId: string | null },
  rowUserId?: string | null,
): boolean {
  const who = preview.detachUserId ?? (visit.assignments.length > 1 ? preview.originUserId : null)
  if (!who) return true
  return !rowUserId || rowUserId === who
}

/** Minutes of work in a set of visits, used by the column headers. */
export function loadOf(visits: BoardVisit[]): number {
  return visits
    .filter((visit) => visit.status !== 'CANCELLED' && visit.status !== 'NO_SHOW')
    .reduce((total, visit) => total + minutesBetween(visit.startAt, visit.endAt), 0)
}
